/**
 * ReportContentModal — Signalement de contenu Try-On (Apple Guideline 1.1/1.2)
 * ───────────────────────────────────────────────────────────────────────────
 * Permet à l'utilisateur de signaler un résultat Try-On (mannequin ou perso).
 * Phase 1 : envoi par email (mailto) à contact@mytenue.com. La version
 * backend (table content_reports + auto-blur) viendra en Phase 2.
 */
import React, { useState } from 'react';
import { View, Text, TouchableOpacity, StyleSheet, Linking, Platform } from 'react-native';
import AppModal from './AppModal';
import { useI18n } from '../i18n';

const REASONS = [
  { key: 'inappropriate', fr: 'Contenu inapproprié',          en: 'Inappropriate content' },
  { key: 'minor',         fr: 'Contient un mineur',           en: 'Contains a minor' },
  { key: 'not_me',        fr: "Ce n'est pas moi sur la photo", en: 'Not me in the photo' },
  { key: 'other',         fr: 'Autre',                        en: 'Other' },
];

export default function ReportContentModal({ visible, onClose, resultUrl = null, context = 'tryon' }) {
  const { lang } = useI18n();
  const isFR = !lang || lang.startsWith('fr');
  const [selected, setSelected] = useState(null);
  const [sent, setSent] = useState(false);

  const submit = async () => {
    if (!selected) return;
    const reason = REASONS.find(r => r.key === selected);
    const reasonLabel = isFR ? reason.fr : reason.en;
    const subject = encodeURIComponent(`[Report] Try-On content — ${reason.key}`);
    const bodyLines = [
      isFR ? 'Signalement de contenu Try-On' : 'Try-On content report',
      `Reason: ${reason.key} (${reasonLabel})`,
      `Context: ${context}`,
      resultUrl ? `Result: ${resultUrl}` : '',
      '',
      isFR ? 'Détails (facultatif) : ' : 'Details (optional): ',
    ].filter(Boolean);
    const body = encodeURIComponent(bodyLines.join('\n'));
    const url = `mailto:contact@mytenue.com?subject=${subject}&body=${body}`;
    try { await Linking.openURL(url); } catch (_) {}
    setSent(true);
  };

  const close = () => { setSelected(null); setSent(false); onClose && onClose(); };

  return (
    <AppModal visible={visible} transparent animationType="fade" onRequestClose={close}>
      <View style={s.overlay}>
        <View style={s.card}>
          <TouchableOpacity onPress={close} style={s.closeBtn} hitSlop={{ top: 10, bottom: 10, left: 10, right: 10 }}>
            <Text style={s.closeTxt}>✕</Text>
          </TouchableOpacity>

          {sent ? (
            <>
              <Text style={s.emoji}>✅</Text>
              <Text style={s.title}>{isFR ? 'Merci pour votre signalement' : 'Thank you for your report'}</Text>
              <Text style={s.sub}>
                {isFR
                  ? 'Notre équipe va examiner ce contenu. Vous pouvez aussi nous écrire à contact@mytenue.com.'
                  : 'Our team will review this content. You can also reach us at contact@mytenue.com.'}
              </Text>
              <TouchableOpacity style={s.cta} onPress={close} activeOpacity={0.85}>
                <Text style={s.ctaTxt}>{isFR ? 'Fermer' : 'Close'}</Text>
              </TouchableOpacity>
            </>
          ) : (
            <>
              <Text style={s.emoji}>🚩</Text>
              <Text style={s.title}>{isFR ? 'Signaler ce contenu' : 'Report this content'}</Text>
              <Text style={s.sub}>{isFR ? 'Pourquoi signalez-vous ce contenu ?' : 'Why are you reporting this content?'}</Text>

              {REASONS.map(r => {
                const active = selected === r.key;
                return (
                  <TouchableOpacity
                    key={r.key}
                    onPress={() => setSelected(r.key)}
                    activeOpacity={0.8}
                    style={[s.reasonRow, active && s.reasonRowActive]}
                  >
                    <View style={[s.radio, active && s.radioActive]}>
                      {active && <View style={s.radioDot} />}
                    </View>
                    <Text style={[s.reasonTxt, active && s.reasonTxtActive]}>{isFR ? r.fr : r.en}</Text>
                  </TouchableOpacity>
                );
              })}

              <TouchableOpacity
                style={[s.cta, !selected && { opacity: 0.45 }]}
                onPress={submit}
                disabled={!selected}
                activeOpacity={0.85}
              >
                <Text style={s.ctaTxt}>{isFR ? 'Envoyer le signalement' : 'Submit report'}</Text>
              </TouchableOpacity>
            </>
          )}
        </View>
      </View>
    </AppModal>
  );
}

const s = StyleSheet.create({
  overlay: { flex: 1, backgroundColor: 'rgba(0,0,0,0.45)', alignItems: 'center', justifyContent: 'center', padding: 20 },
  card: { width: '100%', maxWidth: 380, backgroundColor: '#FFF', borderRadius: 18, padding: 22, alignItems: 'center' },
  closeBtn: { position: 'absolute', top: 10, right: 10, width: 30, height: 30, borderRadius: 15, backgroundColor: '#F2F2F2', alignItems: 'center', justifyContent: 'center' },
  closeTxt: { fontSize: 15, color: '#666', fontWeight: '600' },
  emoji: { fontSize: 34, marginBottom: 8 },
  title: { fontSize: 17, fontWeight: '700', color: '#111', textAlign: 'center', marginBottom: 6 },
  sub: { fontSize: 13, color: '#666', textAlign: 'center', marginBottom: 16, lineHeight: 18 },
  reasonRow: { flexDirection: 'row', alignItems: 'center', width: '100%', paddingVertical: 12, paddingHorizontal: 12, borderRadius: 10, borderWidth: 1, borderColor: '#E5E5E5', marginBottom: 8 },
  reasonRowActive: { borderColor: '#D4AF37', backgroundColor: '#FFFBF0' },
  radio: { width: 20, height: 20, borderRadius: 10, borderWidth: 2, borderColor: '#CCC', marginRight: 12, alignItems: 'center', justifyContent: 'center' },
  radioActive: { borderColor: '#D4AF37' },
  radioDot: { width: 10, height: 10, borderRadius: 5, backgroundColor: '#D4AF37' },
  reasonTxt: { fontSize: 14, color: '#333', flex: 1 },
  reasonTxtActive: { color: '#111', fontWeight: '600' },
  cta: { marginTop: 8, backgroundColor: '#111', paddingVertical: 14, borderRadius: 12, alignItems: 'center', width: '100%' },
  ctaTxt: { color: '#FFF', fontSize: 15, fontWeight: '700' },
});
